/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import cx from 'classnames';
import _get from 'lodash/get';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { CarouselSwitch, CmsSection } from 'src/cms';
import { Spacer, spacerAdapter } from 'src/components-v2/Layout';
import {
  AeoMarkdown,
  HideFromAeo,
} from 'src/components-v3/AeoContent/AeoContent';
import { BackgroundContainer } from 'src/components-v3/BackgroundContainer/BackgroundContainer';
import UnstableContact from 'src/components-v3/Contact/UnstableContact';
import DecorativeStatSection from 'src/components-v3/DecorativeStat/DecorativeStatSection';
import CmsFaq from 'src/components-v3/Faq/CmsFaq';
import CmsPlaceholderSwitch from 'src/components-v3/Placeholder/CmsPlaceholderSwitch';
import Subnav from 'src/components-v3/Subnav/Subnav';
import CmsTable from 'src/components-v3/Table/CmsTable';
import TestimonialSection from 'src/components-v3/Testimonial/TestimonialSection';
import { MarkdownRenderer } from 'src/components/Blog';
import Cta from 'src/components/cta';
import { ctaBlockWithButtonsAdapter } from 'src/components/CtaBlockWithButtons';
import {
  CtaRowWithBrushstroke,
  ctaRowWithBrushstrokeAdapter,
} from 'src/components/CtaRowWithBrushstroke';
import DescriptionRow from 'src/components/description-row';
import { descriptionRowAdapter } from 'src/components/DescriptionRow/adapters';
import {
  FeaturesSection,
  FeaturesSectionItem,
  mvpFeatureSectionAdapter,
} from 'src/components/features/index';
import FooterExtension from 'src/components/footer-extension';
import { formCtaAdapter } from 'src/components/FormCta';
import { FormSection } from 'src/components/Forms';
import { marketoAdapter } from 'src/components/Forms/MarketoForm';
import { GatedContent, toGatedContentProps } from 'src/components/GatedContent';
import {
  Breadcrumb,
  EventHeaderAdapter,
  mvpPageHeaderAdapter,
  PageHeaderCms,
  PageHeaderWithForm,
  pageHeaderWithFormAdapter,
  partnerDirectoryHeaderAdapter,
  partnerPageHeaderAdapter,
  ProductPageHeader,
  productsPageHeaderAdapter,
} from 'src/components/headers';
import { RawHtml } from 'src/components/helpers';
import { Button, Link } from 'src/components/Inputs';
import {
  LandingPageFooter,
  landingPageFooterAdapter,
} from 'src/components/LandingPageFooter';
import {
  AccountTableSection,
  accountTableSectionAdapter,
  Column,
  ContentBlockSectionCms,
  eventTileGroupAdapter,
  Grid,
  Image,
  imageAdapter,
  introSectionAdapter,
  IntroSectionCms,
  mvpContentBlockAdapter,
  mvpQuoteAdapter,
  mvpSocialProofAdapter,
  QuoteCms,
  SocialProofCms,
  SupportingText,
  supportingTextAdapter,
  Tile,
  TileGroup,
  tileGroupAdapter,
} from 'src/components/Layout';
import quoteStyles from 'src/components/Layout/Quote/Quote--cms.module.scss';
import {
  partnerCardsDiverseAdapter,
  PartnerCardsSection,
} from 'src/components/PartnerDirectory/PartnerCards';
import {
  Footer,
  ItemListSection,
  partnerPageFooterAdapter,
} from 'src/components/PartnerDirectory/PartnerPage';
import { RichTextRenderer } from 'src/components/RichTextRenderer';
import { SectionAsset } from 'src/components/SectionAsset';
import {
  blogPostExcerptAdapter,
  CodeSnippet,
  Faq,
  interactiveDataAdapter,
  mvpShowcaseAdapter,
  Paywall,
  productDataAdapter,
  Showcase,
  staticDataAdapter,
  toFaqProps,
  omitPaywallEntries,
  usePaywall,
  usePaywallProvider,
} from 'src/components/Surfaces';
import { VideoEmbed, videoEmbedAdapter } from 'src/components/VideoEmbed';
import { FirstHeroProvider } from 'src/contexts/FirstHeroContext/FirstHeroContext';
import animationStyles from 'src/hooks/useAnimations/useAnimations.module.scss';
import {
  CmsComponents,
  Contentful,
  ContentfulTemplates,
  FormTypes,
  RESOURCES_PATH,
  TileTypes,
} from 'src/lib/constants';
import {
  CmsCtaSwitch,
  CmsEmbeddedVideoCard,
  CmsHeroSwitch,
  CmsLinkCarousel,
  CmsLogos,
  CmsQuoteSwitch,
  CmsSectionalHeader,
  CmsTimeline,
  LeadForm,
  LeftRightSection,
  RichTextHighlightsSection,
} from 'src/sections';
import { CmsCardsSwitch } from 'src/sections/Cards/CmsCardsSwitch';
import { CmsSimpleStatCards } from 'src/sections/Cards/CmsSimpleStatCards';
import articleStyles from 'src/templates/Resources/ArticleTemplate/ArticleTemplate.module.scss';
import blogStyles from './BlogPost.module.scss';
import BlogEmbeddedButton from './BlogEmbeddedButton';
import { CalculatorSwitch } from 'src/components-v3/Calculator/CalculatorSwitch';
import FeatureFlag from 'src/components-v3/FeatureFlag/FeatureFlag';

const StaticData = dynamic(
  () => {
    return import('src/components/Surfaces/StaticData/StaticData.jsx');
  },
  {
    ssr: false,
  },
);
const InteractiveData = dynamic(
  () => {
    return import('src/components/Surfaces/InteractiveData/InteractiveData');
  },
  {
    ssr: false,
  },
);

const propTypes = {
  components: PropTypes.arrayOf(
    PropTypes.shape({
      fields: PropTypes.object,
    }),
  ).isRequired,
  hasAnimations: PropTypes.bool,
};

const ComponentSwitch = (props) => {
  const {
    templatePage,
    components,
    styles,
    hasAnimations,
    isGatedContentHeader,
    gatedContentProps,
    setIsFormSubmitted,
    events,
    search,
    currentEvent,
    topic,
    handleFormSubmit,
    formType,
    trackingId,
    parentKey,
    usePageTheme,
  } = props;
  const inspectorProps = useContentfulInspectorMode();
  const pageComponents = components.map((component, idx) => {
    switch (_get(component, Contentful.CONTENT_TYPE_ID)) {
      case CmsComponents.ACCORDION:
        try {
          return component.fields.includeFaqSchema ? (
            <Faq
              {...toFaqProps(component.fields)}
              key={`${parentKey}faq-${idx}`}
            />
          ) : null;
        } catch {
          return null;
        }
      case CmsComponents.ACCOUNT_TABLE_SECTION:
        try {
          return (
            <AccountTableSection
              key={`${parentKey}acc-table-${idx}`}
              {...accountTableSectionAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.AEO_MARKDOWN:
        return (
          <AeoMarkdown
            key={`${parentKey}aeo-markdown-${idx}`}
            sys={component.sys}
          />
        );
      case CmsComponents.TABLE:
        try {
          return (
            <CmsTable
              key={`${parentKey}table-${idx}`}
              fields={component.fields}
              sys={component.sys}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.CALCULATOR:
        try {
          return (
            <CalculatorSwitch
              key={`${parentKey}calculator-${idx}`}
              {...component}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.ANCHOR:
        try {
          return (
            <span
              id={component.fields.anchorId}
              key={`${parentKey}anchor-${idx}`}
              data-testid={component.fields.anchorId}
            ></span>
          );
        } catch {
          return null;
        }

      case CmsComponents.BLOG_POST_EXCERPT:
        try {
          return (
            <Showcase
              key={`${parentKey}blog-excerpt-${idx}`}
              className='blogPostExcerpt'
              {...blogPostExcerptAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.BUTTON:
        try {
          const { text, url } = component?.fields;
          return (
            <Button
              key={`${parentKey}button-${idx}`}
              href={url}
              entryId={component.sys?.id}
            >
              {text}
            </Button>
          );
        } catch {
          return null;
        }
      case CmsComponents.CARDS_SECTION:
        try {
          return (
            <CmsCardsSwitch
              {...component}
              key={`${parentKey}cards-section-${idx}`}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.SIMPLE_STATS_SECTION:
        try {
          return (
            <CmsSimpleStatCards
              key={`${parentKey}simple-stats-section-${idx}`}
              {...component}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.CODE_SNIPPET:
        try {
          const { syntax, body } = component?.fields;
          return (
            <CodeSnippet
              key={`${parentKey}code-snippet-${idx}`}
              syntax={syntax}
            >
              {body}
            </CodeSnippet>
          );
        } catch {
          return null;
        }
      case CmsComponents.CAROUSEL:
        try {
          return (
            <CmsLinkCarousel
              {...component?.fields}
              sys={component?.sys}
              key={`${parentKey}link-carousel-${idx}`}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.CONTENT_BLOCK:
        try {
          return (
            <ContentBlockSectionCms
              useThreadsGrid={templatePage === ContentfulTemplates.ARTICLE}
              key={`${parentKey}content-block-${idx}`}
              {...mvpContentBlockAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.CTA_BANNER:
        try {
          return (
            <CmsCtaSwitch {...component} key={`${parentKey}ctaBanner-${idx}`} />
          );
        } catch {
          return null;
        }
      case CmsComponents.CTA_BLOCK_WITH_BUTTONS:
        try {
          const adapterData =
            templatePage === ContentfulTemplates.ARTICLE ||
            templatePage === ContentfulTemplates.HUB
              ? { ...ctaBlockWithButtonsAdapter(component.fields) }
              : { ...ctaRowWithBrushstrokeAdapter(component.fields) };
          if (templatePage === ContentfulTemplates.HUB) {
            return (
              <FooterExtension
                key={`${parentKey}footer-extension-${idx}`}
                {...adapterData}
              />
            );
          }
          return (
            <CtaRowWithBrushstroke
              key={`${parentKey}cta-row-${idx}`}
              {...adapterData}
            />
          );
        } catch {
          return null;
        }

      case CmsComponents.DECORATIVE_STAT_SECTION:
        try {
          return (
            <DecorativeStatSection
              key={`${parentKey}decorative-stat-section-${idx}`}
              {...component}
            />
          );
        } catch {
          return null;
        }

      case CmsComponents.DESCRIPTION_ROW:
        try {
          return (
            <DescriptionRow
              {...descriptionRowAdapter(component)}
              key={`${parentKey}description-row-${idx}`}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.EMBEDDED_VIDEO_CARD:
        try {
          return (
            <CmsEmbeddedVideoCard
              {...component}
              key={`${parentKey}embedded-video-card-${idx}`}
            />
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(CmsComponents.EMBEDDED_VIDEO_CARD, 'error:', e);
          return null;
        }
      case CmsComponents.EVENT_LIST:
        try {
          const { tileGroup, ...tileGroupProps } = eventTileGroupAdapter({
            fields: component.fields,
            events,
            currentEvent,
          });

          if (tileGroup?.length > 0) {
            return (
              <TileGroup
                {...tileGroupProps}
                key={`${parentKey}tileGroup-${idx}`}
              >
                {tileGroup.map((tile, idx) => {
                  return <Tile key={`${parentKey}tile-${idx}`} {...tile} />;
                })}
              </TileGroup>
            );
          }
          return null;
        } catch (e) {
          return null;
        }
      case CmsComponents.FEATURE_FLAG:
        try {
          return (
            <FeatureFlag
              key={`${parentKey}feature-flag-${idx}`}
              fields={component.fields}
              sys={component.sys}
            />
          );
        } catch {
          return null;
        }

      case CmsComponents.FEATURES_SECTION:
        try {
          const defaultAssetSize =
            templatePage === ContentfulTemplates.EVENT ? '240x240' : '112x112';
          const fieldsWithAssetSize = {
            ...component.fields,
            assetSize: component.fields.assetSize ?? defaultAssetSize,
          };
          const {
            title,
            hasBetaItems,
            label,
            hasThreeColumns,
            items,
            ...props
          } = mvpFeatureSectionAdapter(fieldsWithAssetSize);
          return (
            <FeaturesSection
              key={`${parentKey}feature-section-${idx}`}
              {...{
                hasBetaItems,
                label,
                title,
                hasThreeColumns,
              }}
              {...props}
            >
              {items.map((item, idx) => {
                return (
                  <FeaturesSectionItem
                    key={`${parentKey}feature-item-${idx}`}
                    hasThreeColumns={hasThreeColumns}
                    {...item}
                  />
                );
              })}
            </FeaturesSection>
          );
        } catch {
          return null;
        }
      case CmsComponents.FORM_CTA:
        try {
          return (
            <Cta
              key={`${parentKey}cta-${idx}`}
              {...formCtaAdapter({
                fields: component.fields,
                template: templatePage,
              })}
              isBodyForm={templatePage === ContentfulTemplates.ARTICLE}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.ITEMS:
        try {
          return (
            <ItemListSection
              key={`${parentKey}item-${idx}`}
              {...component.fields}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.FOOTER:
        try {
          if (templatePage === ContentfulTemplates.PARTNER_PAGE) {
            return (
              <Footer
                key={`${parentKey}partner-footer-${idx}`}
                {...partnerPageFooterAdapter(component.fields)}
              />
            );
          }
          return (
            <LandingPageFooter
              key={`${parentKey}footer-${idx}`}
              {...landingPageFooterAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.GATED_CONTENT:
        try {
          return (
            <GatedContent
              isBodyForm={templatePage === ContentfulTemplates.ARTICLE}
              key={`${parentKey}gated-content-${idx}`}
              gateFields={component.fields.informationGateFormType.fields}
              {...component.fields}
              {...toGatedContentProps(component.fields)}
              setIsFormSubmitted={setIsFormSubmitted}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.GRID_COLUMN:
        return (
          <Column
            key={`${parentKey}column-${idx}`}
            centeredContent={component.fields.centered}
            className={cx(
              styles[component.fields.className],
              styles?.useCaseColumn,
            )}
            small={component.fields.smallColumnWidth}
            medium={component.fields.mediumColumnWidth}
            large={component.fields.largeColumnWidth}
            xlarge={component.fields.extraLargeColumnWidth}
          >
            <CmsComponentSwitch
              isGatedContentHeader={isGatedContentHeader}
              gatedContentProps={gatedContentProps}
              key={`switch-${idx}`}
              {...{ styles, hasAnimations }}
              {...component.fields}
              setIsFormSubmitted={setIsFormSubmitted}
            />
          </Column>
        );
      case CmsComponents.GRID_CONTAINER:
        return (
          <Grid
            key={`${parentKey}grid-${idx}`}
            noPadding={!component.fields.padding}
          >
            <ComponentSwitch
              isGatedContentHeader={isGatedContentHeader}
              gatedContentProps={gatedContentProps}
              key={`switch-${idx}`}
              {...{ styles, hasAnimations }}
              {...component.fields}
              setIsFormSubmitted={setIsFormSubmitted}
            />
          </Grid>
        );
      case CmsComponents.HERO:
        try {
          return (
            <CmsHeroSwitch {...component} key={`${parentKey}hero-${idx}`} />
          );
        } catch {
          return null;
        }
      case CmsComponents.HIDE_FROM_AEO:
        return (
          <HideFromAeo
            {...props}
            {...component}
            key={`${parentKey}hide-from-aeo-${idx}`}
            parentKey={`${parentKey}hide-from-aeo-${idx}`}
          />
        );
      case CmsComponents.IMAGE:
        try {
          if (templatePage === ContentfulTemplates.ARTICLE) {
            return (
              <Grid key={`${parentKey}image-${idx}`} useThreadsGrid>
                <Column small={12}>
                  <Image
                    classes={{
                      figureClassName: cx(articleStyles.figure, {
                        [styles.fullWidth]: component.fields.fullWidth,
                      }),
                      imgClassName: articleStyles.image,
                    }}
                    {...imageAdapter(component.fields)}
                  />
                </Column>
              </Grid>
            );
          } else if (templatePage === ContentfulTemplates.BLOG_POST) {
            return (
              <Image
                key={`${parentKey}image-${idx}`}
                classes={{
                  figureClassName: cx(blogStyles.figure, {
                    [blogStyles.fullWidth]: component?.fields?.fullWidth,
                  }),
                  imgClassName: blogStyles.image,
                }}
                {...imageAdapter(component.fields)}
                entryId={component.sys?.id}
              />
            );
          } else {
            const image = imageAdapter(component.fields);
            return (
              <div
                key={`${parentKey}image-${idx}`}
                className={cx(styles.imageWrapper, {
                  [animationStyles['animated-group__animation']]: hasAnimations,
                  // primary image animations
                  [animationStyles['animated-group__animation--slide-up-40']]:
                    hasAnimations && idx === 0,
                  [animationStyles['animated-group__animation--duration-600']]:
                    hasAnimations && idx === 0,
                  // popup logo
                  [animationStyles[
                    'animated-group__animation--slide-up-bounce'
                  ]]: hasAnimations && idx === 1,
                  [animationStyles['animated-group__animation--delay-600']]:
                    hasAnimations && idx === 1,
                })}
              >
                <div className={styles.imageContainer}>
                  {image?.src && <Image {...image} />}
                </div>
              </div>
            );
          }
        } catch {
          return null;
        }
      // TODO: Remove interactive & static data once instances are replaced
      case CmsComponents.INTERACTIVE_DATA:
        try {
          return (
            <InteractiveData
              key={`${parentKey}interactive-data-${idx}`}
              {...interactiveDataAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.INTRO_SECTION:
        try {
          return (
            <IntroSectionCms
              key={`${parentKey}intro-section-${idx}`}
              {...introSectionAdapter({
                classes: null,
                ...component.fields,
              })}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.LEAD_FORM:
        try {
          return (
            <LeadForm
              key={`${parentKey}cta-${idx}`}
              {...formCtaAdapter({
                fields: component.fields,
                template: 'Lead Form',
              })}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.LEFT_RIGHT_SECTION:
        try {
          return (
            <LeftRightSection
              {...component}
              key={`${parentKey}left-right-section-${idx}`}
            />
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(CmsComponents.LEFT_RIGHT_SECTION, 'error:', e);
          return null;
        }
      case CmsComponents.LEGACY_CAROUSEL:
        return (
          <CarouselSwitch
            key={`${parentKey}legacy-carousel-${idx}`}
            idx={idx}
            component={component}
            templatePage={templatePage}
          />
        );
      case CmsComponents.LOGOS:
        try {
          return <CmsLogos {...component} key={`${parentKey}logos-${idx}`} />;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(CmsComponents.LOGOS, 'error:', e);
          return null;
        }
      case CmsComponents.MARKDOWN:
        try {
          return (
            <MarkdownRenderer
              key={`${parentKey}markdown-${idx}`}
              body={component.fields.markdown}
              entryId={component.sys?.id}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.MARKETO_FORM:
        try {
          const formData = marketoAdapter(component, trackingId);

          return (
            <FormSection
              setIsFormSubmitted={setIsFormSubmitted}
              key={`${parentKey}form-section-${idx}`}
              handleFormSubmit={handleFormSubmit}
              formType={formType}
              usePageTheme={usePageTheme}
              formData={{
                ...formData,
                shouldForward: !Boolean(handleFormSubmit),
              }}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.PAGE_HEADER:
        try {
          const breadcrumb =
            templatePage === ContentfulTemplates.TOPIC ? (
              <Breadcrumb>
                <Link href={RESOURCES_PATH}>
                  <a>RESOURCES</a>
                </Link>
                {topic}
              </Breadcrumb>
            ) : undefined;

          return (
            <PageHeaderCms
              key={`${parentKey}page-header-${idx}`}
              largeHeading={
                templatePage === ContentfulTemplates.HUB ||
                templatePage === ContentfulTemplates.TOPIC
              }
              breadcrumb={breadcrumb}
              search={
                templatePage === ContentfulTemplates.HUB ||
                templatePage === ContentfulTemplates.TOPIC
                  ? search
                  : null
              }
              {...mvpPageHeaderAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.PAGE_HEADER_WITH_FORM:
        try {
          const adapterData =
            templatePage === ContentfulTemplates.EVENT
              ? {
                  ...pageHeaderWithFormAdapter(component.fields),
                  ...EventHeaderAdapter(component, currentEvent),
                }
              : { ...pageHeaderWithFormAdapter(component.fields) };
          return (
            <PageHeaderWithForm
              key={`${parentKey}page-header-with-form-${idx}`}
              {...adapterData}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.PARTNER_DIRECTORY_PAGE_HEADER:
        try {
          return (
            <PageHeaderCms
              key={`${parentKey}page-header-${idx}`}
              {...partnerDirectoryHeaderAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.PARTNER_PAGE_HEADER:
        try {
          return (
            <PageHeaderCms
              key={`${parentKey}page-header-${idx}`}
              {...partnerPageHeaderAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.PARTNERS_LIST:
        try {
          return (
            <PartnerCardsSection
              key={`${parentKey}content-block-${idx}`}
              {...partnerCardsDiverseAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.PRODUCT_DATA_SECTION:
        try {
          if (component.fields.type === 'interactive') {
            return (
              <InteractiveData
                key={`${parentKey}product-data-${idx}`}
                {...productDataAdapter(component.fields)}
              />
            );
          }
          return (
            <StaticData
              key={`${parentKey}product-data-${idx}`}
              {...productDataAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.PRODUCTS_PAGE_HEADER:
        try {
          return (
            <ProductPageHeader
              key={`${parentKey}product-page-header-${idx}`}
              {...productsPageHeaderAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.QUOTE:
        try {
          return (
            <CmsQuoteSwitch {...component} key={`${parentKey}quote-${idx}`} />
          );
        } catch {
          return null;
        }
      case CmsComponents.QUOTE_CMS:
        try {
          return (
            <QuoteCms
              className={
                templatePage === ContentfulTemplates.ARTICLE
                  ? quoteStyles.quotePlain
                  : ''
              }
              key={`${parentKey}quote-${idx}`}
              {...mvpQuoteAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.RAW_HTML:
        try {
          return (
            <RawHtml
              key={`${parentKey}raw-html-${idx}`}
              entryId={component.sys?.id}
              {...component.fields}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.RICH_TEXT_COMPONENT:
        try {
          if (templatePage === ContentfulTemplates.ARTICLE) {
            return (
              <Grid
                className={articleStyles.richTextContainer}
                key={`${parentKey}rich-text-${idx}`}
                useThreadsGrid
              >
                <Column medium={1} large={2} />
                <Column medium={10} large={8}>
                  <RichTextRenderer
                    classes={{ aClassName: articleStyles.hyperlink }}
                    content={component.fields.body}
                  />
                </Column>
                <Column medium={1} large={2} />
              </Grid>
            );
          }
          return (
            <RichTextRenderer
              key={`${parentKey}rich-text-${idx}`}
              content={component.fields.body}
              renderEmbeddedButton={
                templatePage === ContentfulTemplates.BLOG_POST
                  ? BlogEmbeddedButton
                  : undefined
              }
              {...inspectorProps({
                entryId: component.sys?.id,
                fieldId: 'body',
              })}
              className={cx(styles?.richTextContainer, {
                [animationStyles['animated-group__animation']]: hasAnimations,
                [animationStyles['animated-group__animation--slide-up-16']]:
                  hasAnimations,
                [animationStyles['animated-group__animation--duration-300']]:
                  hasAnimations,
              })}
              classes={{
                h1ClassName: styles?.h1ClassName,
                h2ClassName: styles?.h2ClassName,
                h4ClassName: styles?.h4ClassName,
                h6ClassName: styles?.h6ClassName,
                pClassName: styles?.pClassName,
                divClassName:
                  templatePage === ContentfulTemplates.BLOG_POST
                    ? blogStyles.wrapper
                    : styles?.divClassName,
                buttonClassName: styles?.buttonClassName,
                ulClassName: blogStyles.list,
                olClassName: blogStyles.list,
                imgClassName: blogStyles.image,
                figureClassName: cx(blogStyles.figure, {
                  [blogStyles.fullWidth]: component?.fields?.fullWidth,
                }),
                aClassName: blogStyles.hyperlink,
              }}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.RICH_TEXT_HIGHLIGHTS_SECTION:
        try {
          return (
            <RichTextHighlightsSection
              {...component}
              key={`${parentKey}rtHighlightsSection-${idx}`}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.SECTION:
        try {
          return (
            <CmsSection {...component} key={`${parentKey}cmsSection-${idx}`} />
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(CmsComponents.SECTION, 'error:', e);
          return null;
        }
      case CmsComponents.SHOWCASE:
        try {
          return (
            <Showcase
              key={`${parentKey}showcase-${idx}`}
              {...mvpShowcaseAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.SECTIONAL_HEADER:
        try {
          return (
            <CmsSectionalHeader
              {...component}
              key={`${parentKey}sectional-header-${idx}`}
            />
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(CmsComponents.SECTIONAL_HEADER, 'error:', e);
          return null;
        }
      case CmsComponents.SOCIAL_PROOF:
        try {
          return (
            <SocialProofCms
              key={`${parentKey}social-proof-${idx}`}
              {...mvpSocialProofAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.SPACER:
        try {
          return (
            <Spacer
              key={`${parentKey}spacer-${idx}`}
              {...spacerAdapter(component)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.STATIC_DATA:
        try {
          return (
            <StaticData
              key={`${parentKey}static-data-${idx}`}
              {...staticDataAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }

      case CmsComponents.SUBNAV:
        try {
          return (
            <Subnav
              key={`${parentKey}subnav-${idx}`}
              fields={component.fields}
              sys={component.sys}
            />
          );
        } catch {
          return null;
        }

      case CmsComponents.SUPPORTING_TEXT:
        try {
          return (
            <SupportingText
              useThreadsGrid={templatePage === ContentfulTemplates.ARTICLE}
              key={`${parentKey}supporting-text-${idx}`}
              {...supportingTextAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.TILE_GROUP:
        try {
          const { tileGroup, ...tileGroupProps } = tileGroupAdapter(
            component.fields,
          );
          return (
            <TileGroup
              {...tileGroupProps}
              key={`${parentKey}tile-group-${idx}`}
              useThreadsGrid={templatePage === ContentfulTemplates.ARTICLE}
            >
              {tileGroup.map((tile, idx) => {
                return (
                  <Tile
                    key={`${parentKey}${
                      tile.type === TileTypes.PEOPLE_SPOTLIGHT
                        ? 'people-spotlight-'
                        : ''
                    }tile-${idx}`}
                    {...tile}
                  />
                );
              })}
            </TileGroup>
          );
        } catch {
          return null;
        }
      case CmsComponents.TESTIMONIAL_SECTION:
        try {
          return (
            <TestimonialSection
              {...component}
              key={`${parentKey}testimonial-section-${idx}`}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.TIMELINE:
        try {
          return (
            <CmsTimeline {...component} key={`${parentKey}timeline-${idx}`} />
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log('something broke in the timeline', e);
          return null;
        }
      case CmsComponents.VIDEO_EMBED:
        try {
          return (
            <VideoEmbed
              className={
                templatePage === ContentfulTemplates.ARTICLE
                  ? articleStyles.figure
                  : ''
              }
              isLazy={templatePage === ContentfulTemplates.ARTICLE}
              useThreadsGrid={templatePage === ContentfulTemplates.ARTICLE}
              key={`${parentKey}embedded-video-${idx}`}
              {...videoEmbedAdapter(component.fields)}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.SECTION_ASSET: {
        try {
          return <SectionAsset {...component} key={`section-asset-${idx}`} />;
        } catch {
          return null;
        }
      }
      case CmsComponents.BACKGROUND_CONTAINER:
        try {
          return (
            <BackgroundContainer
              {...props}
              {...component}
              key={`${parentKey}bg-container-${idx}`}
              parentKey={`${parentKey}bg-container-${idx}`}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.CONTACT_FORM:
        try {
          return (
            <UnstableContact
              key={`${parentKey}contact-form-${idx}`}
              {...component}
            />
          );
        } catch {
          return null;
        }
      case CmsComponents.PLACEHOLDER:
        return (
          <CmsPlaceholderSwitch
            key={`${parentKey}placeholder-${idx}`}
            fields={component.fields}
            sys={component.sys}
          />
        );
      case CmsComponents.SECTION_FAQ:
        try {
          return (
            <CmsFaq
              sys={component?.sys}
              fields={component?.fields}
              key={`${parentKey}faq-section-${idx}`}
            />
          );
        } catch {
          return null;
        }
      default:
        return null;
    }
  });
  return pageComponents;
};

const CmsComponentSwitch = ({
  templatePage = '',
  components = [],
  styles = {},
  hasAnimations = false,
  isGatedContentHeader = false,
  gatedContentProps = {},
  setIsFormSubmitted = null,
  filterResults = null,
  role = '',
  totalJobCount = {},
  hasSearchButton = false,
  events = [],
  search = null,
  currentEvent = {},
  topic = '',
  fields = {},
  handleFormSubmit = () => {
    return null;
  },
  formType = FormTypes.Legacy,
  trackingId = '',
  parentKey = '',
  usePageTheme = false,
}) => {
  const { hasPaywall, visibleComponents, hiddenComponents, paywallComponent } =
    usePaywall(components);

  const { isContentUnlocked } = usePaywallProvider();
  // The locked split copies a background container. Once unlocked, render the
  // original container with the paywall removed so the gradient stays continuous.
  const renderedComponents =
    hasPaywall && isContentUnlocked
      ? omitPaywallEntries(components)
      : visibleComponents;

  return (
    <FirstHeroProvider components={components}>
      <ComponentSwitch
        templatePage={templatePage}
        components={renderedComponents}
        fields={fields}
        hasPaywall={hasPaywall}
        isContentUnlocked={isContentUnlocked}
        isBelowThePaywall={false}
        handleFormSubmit={handleFormSubmit}
        styles={styles}
        hasAnimations={hasAnimations}
        isGatedContentHeader={isGatedContentHeader}
        gatedContentProps={gatedContentProps}
        setIsFormSubmitted={setIsFormSubmitted}
        filterResults={filterResults}
        role={role}
        totalJobCount={totalJobCount}
        hasSearchButton={hasSearchButton}
        events={events}
        search={search}
        currentEvent={currentEvent}
        topic={topic}
        formType={formType}
        trackingId={trackingId}
        parentKey={parentKey}
        usePageTheme={usePageTheme}
      />
      {hasPaywall && !isContentUnlocked && (
        <Paywall
          button={{
            buttonText:
              paywallComponent?.fields?.paywallButtonText || 'Read full story',
          }}
          fields={paywallComponent.fields}
          sys={paywallComponent.sys}
          trackingId='CUSTOMER_STORY' // right now customer story is the only page that uses the paywall
        >
          <div className='paywall'>
            <ComponentSwitch
              templatePage={templatePage}
              components={hiddenComponents}
              fields={fields}
              hasPaywall={hasPaywall}
              isContentUnlocked={isContentUnlocked}
              isBelowThePaywall={true}
              handleFormSubmit={handleFormSubmit}
              styles={styles}
              hasAnimations={hasAnimations}
              isGatedContentHeader={isGatedContentHeader}
              gatedContentProps={gatedContentProps}
              setIsFormSubmitted={setIsFormSubmitted}
              filterResults={filterResults}
              role={role}
              totalJobCount={totalJobCount}
              hasSearchButton={hasSearchButton}
              events={events}
              search={search}
              currentEvent={currentEvent}
              topic={topic}
              formType={formType}
              trackingId={trackingId}
              parentKey={parentKey}
              usePageTheme={usePageTheme}
            />
          </div>
        </Paywall>
      )}
    </FirstHeroProvider>
  );
};
CmsComponentSwitch.propTypes = propTypes;

export default CmsComponentSwitch;
